import * as React from 'react'
import { CreateInvoiceDetail } from '../InvoiceDetailStore'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { inject, observer } from 'mobx-react'
import { DatePicker, Input, Icon, Tag, Row, Tooltip, Select } from 'antd'
import { Descriptions } from 'antd-v4'
import moment from 'moment'
import { Currency } from '../../../../../components/Currency'
import { PaymentStatus } from './PaymentStatus'
import { BlazeButton } from '../../../../../components/CustomButtons/BlazeButton'
import { CurrencyInput } from '../../../../../components/CurrencyInput'
import { OptionProps } from 'antd/lib/select'

interface Props {
  createInvoice: CreateInvoiceDetail
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

export const InvoiceDescription = inject('currentCompanyStore')(
  observer((props: Props & InjectedProps) => {
    const { createInvoice, currentCompanyStore } = props
    const { isEditMode, isEditable, invoiceDetail, paymentDueDate, orderDate, renderedStatus } =
      createInvoice

    const createDate = invoiceDetail && invoiceDetail.createDate
    const completeDate = invoiceDetail && invoiceDetail.completeDate
    const startingUserName = invoiceDetail && invoiceDetail.startingUserName
    const paid = invoiceDetail && invoiceDetail && invoiceDetail.paid

    const locations = currentCompanyStore!.locations || []
    const selectedLocation = locations.find((location) => location.id === createInvoice.locationId)

    const suppliers = (currentCompanyStore!.supplierStore!.suppliers || []).filter(
      (supplier) => supplier.name !== '',
    )
    const selectedSupplier = suppliers.find((supplier) => supplier.id === createInvoice.supplierId)

    const taxGroups = currentCompanyStore!.productStore!.taxGroups || []
    const selectedTaxGroup = taxGroups.find((taxGroup) => {
      return createInvoice.taxGroup && taxGroup.id === createInvoice.taxGroup.id
    })

    const filterOption = (input: string, option: React.ReactElement<OptionProps>) => {
      const optionName = option.props.children

      if (optionName && typeof optionName === 'string') {
        const lowerCaseInput = input.toLowerCase()

        return optionName.toLowerCase().indexOf(lowerCaseInput) >= 0
      }

      return true
    }

    return (
      <Descriptions
        bordered
        size='small'
        column={{ lg: 2, md: 1, sm: 1, xs: 1 }}
        className='white-rounded-container'
      >
        <Descriptions.Item label='ID'>
          {createInvoice.invoiceDetail?.customId || '-'}
        </Descriptions.Item>
        <Descriptions.Item label='Create date'>
          {createDate ? moment(createDate).format('MMM D, YYYY hh:mm a') : '-'}
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <span>
              Title
              {isEditMode && <sup className='text-danger ml-1'>* required</sup>}
            </span>
          }
        >
          {isEditMode ? (
            <Input
              rows={1}
              value={createInvoice.title}
              placeholder='Add a title'
              onChange={(e) => createInvoice.setTitle(e.target.value)}
            />
          ) : (
            createInvoice.title
          )}
        </Descriptions.Item>
        <Descriptions.Item label='Order date'>
          {isEditMode ? (
            <DatePicker
              allowClear
              className='w-100'
              placeholder='Select a date'
              format={['MMM D, YYYY']}
              value={orderDate ? moment(orderDate).startOf('day') : undefined}
              onChange={(date) => {
                createInvoice.setOrderDate(date ? date.toDate() : null)
              }}
            />
          ) : orderDate ? (
            moment(orderDate).startOf('day').format('MMM D, YYYY')
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <span>
              Location
              {isEditMode && <sup className='text-danger ml-1'>* required</sup>}
            </span>
          }
          style={{ maxWidth: '20vh' }}
        >
          {isEditMode && isEditable ? (
            <Select<number>
              showSearch
              className='w-100'
              placeholder='Select a location'
              dropdownMatchSelectWidth={false}
              value={createInvoice.locationId}
              onChange={(value) => createInvoice.setLocationId(value)}
              filterOption={filterOption}
            >
              {locations.map((location, index) => (
                <Select.Option key={index} value={location.id}>
                  {location.name}
                </Select.Option>
              ))}
            </Select>
          ) : selectedLocation ? (
            selectedLocation.name
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label='Complete date'>
          {completeDate ? moment(completeDate).format('MMM D, YYYY hh:mm a') : '-'}
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <span>
              Vendor
              {isEditMode && (
                <Tooltip title='Select provincial distributor for cannabis products (except SK)'>
                  <Icon className='ml-2' type='exclamation-circle' />
                </Tooltip>
              )}
            </span>
          }
          style={{ maxWidth: '20vh' }}
        >
          {isEditMode ? (
            <Select<number | null>
              showSearch
              className='w-100'
              placeholder='Select a vendor'
              dropdownMatchSelectWidth={false}
              allowClear
              value={createInvoice.supplierId || undefined}
              onChange={(value) => {
                createInvoice.setSupplierId(value === undefined ? null : value)
              }}
              filterOption={filterOption}
            >
              {suppliers.map((supplier, index) => (
                <Select.Option key={index} value={supplier.id}>
                  {supplier.name}
                </Select.Option>
              ))}
            </Select>
          ) : selectedSupplier ? (
            selectedSupplier.name
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label='Payment due date'>
          {isEditMode ? (
            <DatePicker
              allowClear
              className='w-100'
              placeholder='Select a date'
              format={['MMM D, YYYY']}
              value={paymentDueDate ? moment(paymentDueDate).startOf('day') : undefined}
              onChange={(date) => {
                createInvoice.setPaymentDueDate(date ? date.toDate() : null)
              }}
            />
          ) : paymentDueDate ? (
            moment(paymentDueDate).startOf('day').format('LL')
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label='Status'>
          {renderedStatus ? <Tag color={renderedStatus.color}>{renderedStatus.name}</Tag> : '-'}
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <Row className='d-flex align-items-center'>
              <span>Paid</span>

              {invoiceDetail && (
                <PaymentStatus
                  className='ml-2 mr-0'
                  invoicePaid={invoiceDetail.paid}
                  invoiceTotal={invoiceDetail.total}
                />
              )}
            </Row>
          }
        >
          {isEditMode ? (
            <Row>
              <CurrencyInput
                className='w-50 mr-2 gr-currency-input'
                centValue={createInvoice.paid}
                onChange={(value) => createInvoice.setPaid(value || 0)}
                onlyPositiveNumber
              />
              <BlazeButton
                buttonProps={{
                  onClick: () => (createInvoice.paid = createInvoice.total),
                }}
                titleProps={{
                  title: 'Set as fully paid',
                }}
              />
            </Row>
          ) : (
            <Currency centValue={paid || 0} />
          )}
        </Descriptions.Item>
        <Descriptions.Item label='Tax group' style={{ maxWidth: '20vh' }}>
          {isEditMode ? (
            <Select<number>
              showSearch
              className='w-100'
              placeholder='Select a tax group'
              dropdownMatchSelectWidth={false}
              allowClear
              value={createInvoice.taxGroup ? createInvoice.taxGroup.id : undefined}
              onChange={(value) => {
                if (value === undefined) {
                  createInvoice.setTaxGroup(null)
                }

                const foundTaxGroup = taxGroups.find((taxGroup) => taxGroup.id === value)

                if (foundTaxGroup) {
                  createInvoice.setTaxGroup(foundTaxGroup)
                }
              }}
              filterOption={filterOption}
            >
              {taxGroups.map((taxGroup, index) => (
                <Select.Option key={index} value={taxGroup.id}>
                  {taxGroup.name}
                </Select.Option>
              ))}
            </Select>
          ) : selectedTaxGroup ? (
            selectedTaxGroup.name
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label='Employee'>{startingUserName || '-'}</Descriptions.Item>
        <Descriptions.Item label='Notes'>
          {isEditMode ? (
            <Input.TextArea
              rows={1}
              value={createInvoice.notes || undefined}
              placeholder='Add a note'
              onChange={(e) => createInvoice.setNotes(e.target.value)}
            />
          ) : (
            createInvoice.notes || '-'
          )}
        </Descriptions.Item>
      </Descriptions>
    )
  }),
)
